import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@satyrsmc/shared/components";
import { Plus, FileInput } from "lucide-react";
import { useAppState } from "@/state/AppState";
import { LineItemRow } from "./LineItemRow";
import { ImportLineItemsModal } from "./ImportLineItemsModal";
import { NewLineItemModal } from "./NewLineItemModal";
import type { LineItem } from "@satyrsmc/shared/client";

export function LineItemsTable() {
  const {
    getLineItems,
    addLineItem,
    updateLineItem,
    deleteLineItem,
    addCategory,
    categories,
    selectedBudgetId,
    currentBudget,
    budgets,
    refreshBudget,
  } = useAppState();
  const lineItems = getLineItems();
  const [importOpen, setImportOpen] = useState(false);
  const [newItemOpen, setNewItemOpen] = useState(false);

  const handleImport = async (items: Omit<LineItem, "id">[]) => {
    if (!selectedBudgetId) return;
    for (const item of items) {
      await addLineItem(selectedBudgetId, item);
    }
    await refreshBudget(selectedBudgetId);
  };

  const handleCreate = async (item: Omit<LineItem, "id">) => {
    if (!selectedBudgetId) return;
    await addLineItem(selectedBudgetId, item);
  };

  if (!selectedBudgetId || !currentBudget) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Select a budget above to view and edit line items.
        </CardContent>
      </Card>
    );
  }

  const total = lineItems.reduce((sum, li) => sum + li.unitCost * li.quantity, 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Budget Line Items</CardTitle>
          <CardDescription>
            Add, edit, or remove expense items. Use unit cost and quantity for variable costs.
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setImportOpen(true)}
            title="Import line items from another budget"
          >
            <FileInput className="size-4" />
            Import
          </Button>
          <Button onClick={() => setNewItemOpen(true)} size="sm">
            <Plus className="size-4" />
            Add Item
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="max-sm:py-3">Name</TableHead>
                <TableHead className="max-sm:py-3">Unit Cost</TableHead>
                <TableHead className="max-sm:py-3">Qty</TableHead>
                <TableHead className="text-right max-sm:py-3">Total</TableHead>
                <TableHead className="max-sm:py-3">Category</TableHead>
                <TableHead className="max-sm:py-3">Comments</TableHead>
                <TableHead className="min-w-[72px] max-sm:py-3" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineItems.map((item) => (
                <LineItemRow
                  key={item.id}
                  item={item}
                  categories={categories}
                  onUpdate={(id, updates) => updateLineItem(selectedBudgetId, id, updates)}
                  onDelete={(id) => deleteLineItem(selectedBudgetId, id)}
                  onAddCategory={addCategory}
                />
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="mt-4 flex justify-end border-t pt-4">
          <span className="text-lg font-semibold">
            Total: ${total.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </span>
        </div>
      </CardContent>
      <ImportLineItemsModal
        open={importOpen}
        onOpenChange={setImportOpen}
        currentBudgetId={selectedBudgetId}
        budgets={budgets}
        onImport={handleImport}
      />
      <NewLineItemModal
        open={newItemOpen}
        onOpenChange={setNewItemOpen}
        categories={categories}
        onAddCategory={addCategory}
        onCreate={handleCreate}
      />
    </Card>
  );
}
